import React, { useState, useMemo } from 'react';
import ShortLinkField from './ShortLinkField';
import { EmailPreviewFrame } from './email/EmailEditor';

const MAX_CHARS = 160;
const csrfToken = () => document.querySelector('[name="csrf-token"]')?.content;
const jsonHeaders = () => ({ 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() });

const preview = (text, limit = 90) =>
  text.length > limit ? `${text.slice(0, limit)}…` : text;

const MessageBubble = ({ text }) => (
  <div className='bg-slate-100 p-4 rounded-lg'>
    <div className='bg-white rounded-lg p-4 shadow-sm max-w-sm'>
      <div className='text-slate-900 whitespace-pre-wrap break-words'>
        {text || 'Your message will appear here…'}
      </div>
    </div>
  </div>
);

const TemplateForm = ({ initial, onSaved, onCancel }) => {
  const [name, setName] = useState(initial?.name || '');
  const [text, setText] = useState(initial?.text || '');
  const [link, setLink] = useState({
    destinationUrl: initial?.destination_url || '',
    shortUrl: initial?.short_url || '',
    rebrandlyId: initial?.rebrandly_id || '',
    slashtag: initial?.slashtag || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!name.trim() || !text.trim()) {
      setError('Name and message text are required.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const editing = !!initial?.id;
      const res = await fetch(editing ? `/sms_templates/${initial.id}` : '/sms_templates', {
        method: editing ? 'PATCH' : 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({
          sms_template: {
            name,
            text,
            destination_url: link.destinationUrl || null,
            short_url: link.shortUrl || null,
            rebrandly_id: link.rebrandlyId || null,
            slashtag: link.slashtag || null
          }
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error((data.errors || []).join('. ') || 'Save failed');

      onSaved(data, editing);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <div className='space-y-4'>
      <div>
        <label className='block text-xs font-medium text-slate-500 mb-1'>Template name *</label>
        <input
          className='shadow-sm border border-slate-300 rounded px-2 py-1.5 text-sm w-full'
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder='e.g. Live match bonus'
        />
      </div>

      <div>
        <div className='flex justify-between items-center mb-1'>
          <label className='block text-xs font-medium text-slate-500'>Message text *</label>
          <span className={`text-xs ${text.length > MAX_CHARS - 20 ? 'text-red-500' : 'text-slate-400'}`}>
            {text.length}/{MAX_CHARS}
          </span>
        </div>
        <textarea
          rows='4'
          className='shadow-sm border border-slate-300 rounded px-2 py-1.5 text-sm w-full'
          value={text}
          onChange={(e) => e.target.value.length <= MAX_CHARS && setText(e.target.value)}
          placeholder='Enter your message text here…'
        />
      </div>

      <ShortLinkField
        value={link}
        title={name || 'SMS template'}
        onChange={setLink}
        hint='The campaign gets its own short link to the same destination, so clicks stay attributed per campaign.'
      />

      <div>
        <p className='text-xs font-medium text-slate-500 mb-1'>Preview</p>
        <MessageBubble text={text} />
      </div>

      {error && <p className='text-sm text-red-600'>{error}</p>}

      <div className='flex justify-end gap-2 pt-2'>
        <button onClick={onCancel} className='px-3 py-2 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 rounded'>
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={saving}
          className={`px-3 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded ${saving ? 'opacity-50' : ''}`}
        >
          {saving ? 'Saving…' : initial?.id ? 'Save changes' : 'Create template'}
        </button>
      </div>
    </div>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
    <div
      className='bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto'
      onClick={(e) => e.stopPropagation()}
    >
      <div className='flex items-center justify-between px-5 py-3 border-b border-slate-200'>
        <h3 className='text-base font-semibold text-slate-900'>{title}</h3>
        <button onClick={onClose} className='text-slate-400 hover:text-slate-600'>✕</button>
      </div>
      <div className='p-5'>{children}</div>
    </div>
  </div>
);

const ChannelBadge = ({ channel }) => (
  <span
    className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${
      channel === 'email' ? 'bg-violet-50 text-violet-700' : 'bg-emerald-50 text-emerald-700'
    }`}
  >
    {channel === 'email' ? 'Email' : 'SMS'}
  </span>
);

const EmailPreviewPanel = ({ template, onClose }) => {
  const [device, setDevice] = useState('desktop');
  return (
    <div className='fixed inset-0 z-50 flex justify-end bg-black/30'>
      <div className='bg-white w-full max-w-4xl h-full overflow-y-auto shadow-xl' onClick={(e) => e.stopPropagation()}>
        <div className='flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10'>
          <div className='min-w-0'>
            <div className='flex items-center gap-2'>
              <ChannelBadge channel='email' />
              <h3 className='text-lg font-semibold text-slate-900 truncate'>{template.name}</h3>
            </div>
            <p className='text-sm text-slate-900 mt-1'>
              <span className='text-slate-500'>Subject:</span> {template.subject}
            </p>
            {template.preheader && (
              <p className='text-sm text-slate-500'>
                <span>Preheader:</span> {template.preheader}
              </p>
            )}
            {!template.sendable && (
              <p className='mt-1 text-xs text-red-600'>No Unsubscribe link — this email cannot be used in a campaign.</p>
            )}
          </div>
          <div className='flex items-center gap-2 shrink-0'>
            <div className='flex bg-slate-100 rounded-md p-0.5'>
              {['desktop', 'mobile'].map((d) => (
                <button
                  key={d}
                  onClick={() => setDevice(d)}
                  className={`px-2.5 py-1 text-xs rounded ${device === d ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}
                >
                  {d === 'desktop' ? 'Desktop' : 'Mobile'}
                </button>
              ))}
            </div>
            <a href={`/email_templates/${template.id}/edit`} className='px-3 py-1.5 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded'>
              Edit
            </a>
            <button onClick={onClose} className='text-slate-400 hover:text-slate-600 px-1'>✕</button>
          </div>
        </div>
        <div className='p-6'>
          <EmailPreviewFrame html={template.html} device={device} height={900} />
        </div>
      </div>
    </div>
  );
};

const SmsTemplates = ({ initialTemplates, initialEmailTemplates, initialChannel }) => {
  const [templates, setTemplates] = useState(initialTemplates || []);
  const [emailTemplates, setEmailTemplates] = useState(initialEmailTemplates || []);
  const [channel, setChannel] = useState(initialChannel || 'all');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [emailPreviewId, setEmailPreviewId] = useState(null);
  const [choosing, setChoosing] = useState(false);

  const visible = useMemo(() => {
    const q = search.toLowerCase();
    const all = [
      ...templates.map((t) => ({ ...t, channel: 'sms' })),
      ...emailTemplates.map((t) => ({ ...t, channel: 'email' }))
    ];
    return all
      .filter((t) => channel === 'all' || t.channel === channel)
      .filter((t) => !q || t.name.toLowerCase().includes(q))
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  }, [templates, emailTemplates, channel, search]);

  const emailPreviewed = emailTemplates.find((t) => t.id === emailPreviewId) || null;

  const removeEmail = async (template) => {
    const used = template.campaigns_count
      ? `\n\nIt was used by ${template.campaigns_count} campaign(s). They keep their own copy of the email.`
      : '';
    if (!window.confirm(`Delete email template “${template.name}”?${used}`)) return;

    await fetch(`/email_templates/${template.id}`, { method: 'DELETE', headers: jsonHeaders() });
    setEmailTemplates((prev) => prev.filter((t) => t.id !== template.id));
    if (emailPreviewId === template.id) setEmailPreviewId(null);
  };

  const previewed = templates.find((t) => t.id === previewId) || null;

  const onSaved = (template, editing) => {
    setTemplates((prev) =>
      editing ? prev.map((t) => (t.id === template.id ? template : t)) : [template, ...prev]
    );
    setModal(null);
    setPreviewId(template.id);
  };

  const remove = async (template) => {
    const used = template.campaigns_count
      ? `\n\nIt was used by ${template.campaigns_count} campaign(s). They keep their own copy of the text and link.`
      : '';
    if (!window.confirm(`Delete template “${template.name}”?${used}`)) return;

    await fetch(`/sms_templates/${template.id}`, { method: 'DELETE', headers: jsonHeaders() });
    setTemplates((prev) => prev.filter((t) => t.id !== template.id));
    if (previewId === template.id) setPreviewId(null);
  };

  return (
    <div className='min-h-screen bg-slate-50/50'>
      <div className='max-w-[1400px] mx-auto px-6 py-8'>
        <div className='flex items-center justify-between mb-6'>
          <div>
            <h1 className='text-2xl font-semibold text-slate-900'>Messages</h1>
            <p className='mt-1 text-sm text-slate-600'>
              Saved SMS and email templates — reuse them when creating a campaign.
            </p>
          </div>
          <div className='relative'>
            <button
              onClick={() => setChoosing(!choosing)}
              className='px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 rounded-lg shadow-sm'
            >
              + New template
            </button>
            {choosing && (
              <div className='absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-30 p-1'>
                <p className='px-3 pt-2 pb-1 text-xs text-slate-500'>Choose a channel</p>
                <button
                  onClick={() => { setChoosing(false); setModal({ mode: 'create' }); }}
                  className='w-full text-left px-3 py-2 rounded hover:bg-slate-50'
                >
                  <div className='text-sm font-medium text-slate-900'>SMS</div>
                  <div className='text-xs text-slate-500'>Short text with a tracked link</div>
                </button>
                <a href='/email_templates/new' className='block px-3 py-2 rounded hover:bg-slate-50'>
                  <div className='text-sm font-medium text-slate-900'>Email</div>
                  <div className='text-xs text-slate-500'>Visual / HTML email builder</div>
                </a>
              </div>
            )}
          </div>
        </div>

        <div className='bg-white rounded-xl border border-slate-200/70 shadow-sm p-4 mb-6 flex flex-wrap items-end gap-4'>
          <div className='flex-1 min-w-[240px]'>
            <label className='block text-xs font-medium text-slate-500 mb-1'>Search by name</label>
            <input
              className='shadow-sm border border-slate-300 rounded px-3 py-2 text-sm w-full'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Template name…'
            />
          </div>
          <div>
            <label className='block text-xs font-medium text-slate-500 mb-1'>Type</label>
            <div className='flex bg-slate-100 rounded-lg p-1'>
              {[['all', 'All'], ['sms', 'SMS'], ['email', 'Email']].map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setChannel(value)}
                  className={`px-3 py-1.5 text-sm rounded-md ${channel === value ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-600'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className='bg-white rounded-xl border border-slate-200/70 shadow-sm overflow-hidden'>
          <div className='px-4 py-2 border-b border-slate-200 bg-slate-50/50'>
            <span className='text-xs font-medium text-slate-500 uppercase'>
              {visible.length} template{visible.length === 1 ? '' : 's'}
            </span>
          </div>

          <div className='divide-y divide-slate-100'>
            {visible.map((t) => t.channel === 'email' ? (
              <div key={`email-${t.id}`} className='flex items-center gap-3 px-4 py-3 hover:bg-slate-50'>
                <div className='min-w-0 flex-1'>
                  <div className='flex items-center gap-2'>
                    <ChannelBadge channel='email' />
                    <span className='font-medium text-slate-900'>{t.name}</span>
                    {!t.sendable && (
                      <span className='text-xs px-2 py-0.5 rounded bg-red-50 text-red-700'>no unsubscribe</span>
                    )}
                    {t.campaigns_count > 0 && (
                      <span className='text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600'>
                        used in {t.campaigns_count}
                      </span>
                    )}
                  </div>
                  <div className='text-sm text-slate-700 truncate'>
                    <span className='text-slate-400'>Subject:</span> {t.subject}
                  </div>
                  <div className='text-sm text-slate-500 truncate'>{t.preview_text}</div>
                </div>

                <span className='text-xs text-slate-400 whitespace-nowrap text-right'>
                  <div>Updated {new Date(t.updated_at).toLocaleString()}</div>
                  <div>Created {new Date(t.created_at).toLocaleDateString()}</div>
                </span>

                <button onClick={() => setEmailPreviewId(t.id)} className='text-sm text-blue-600 hover:text-blue-800'>
                  Preview
                </button>
                <a href={`/email_templates/${t.id}/edit`} className='text-sm text-slate-600 hover:text-slate-900'>
                  Edit
                </a>
                <button onClick={() => removeEmail(t)} className='text-sm text-red-600 hover:text-red-800'>
                  Delete
                </button>
              </div>
            ) : (
              <div key={`sms-${t.id}`} className='flex items-center gap-3 px-4 py-3 hover:bg-slate-50'>
                <div className='min-w-0 flex-1'>
                  <div className='flex items-center gap-2'>
                    <ChannelBadge channel='sms' />
                    <span className='font-medium text-slate-900'>{t.name}</span>
                    {t.short_url && (
                      <span className='text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700'>link</span>
                    )}
                    {t.campaigns_count > 0 && (
                      <span className='text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600'>
                        used in {t.campaigns_count}
                      </span>
                    )}
                  </div>
                  <div className='text-sm text-slate-500 truncate'>{preview(t.text)}</div>
                </div>

                <span className='text-xs text-slate-400 whitespace-nowrap'>
                  {new Date(t.updated_at).toLocaleString()}
                </span>

                <button
                  onClick={() => setPreviewId(t.id)}
                  className='text-sm text-blue-600 hover:text-blue-800'
                >
                  Preview
                </button>
                <button
                  onClick={() => setModal({ mode: 'edit', template: t })}
                  className='text-sm text-slate-600 hover:text-slate-900'
                >
                  Edit
                </button>
                <button onClick={() => remove(t)} className='text-sm text-red-600 hover:text-red-800'>
                  Delete
                </button>
              </div>
            ))}

            {visible.length === 0 && (
              <div className='px-4 py-10 text-center text-sm text-slate-500'>
                {templates.length + emailTemplates.length ? 'Nothing matches the filters.' : 'No templates yet.'}
              </div>
            )}
          </div>
        </div>
      </div>

      {modal && (
        <Modal
          title={modal.mode === 'edit' ? 'Edit template' : 'New SMS template'}
          onClose={() => setModal(null)}
        >
          <TemplateForm initial={modal.template} onSaved={onSaved} onCancel={() => setModal(null)} />
        </Modal>
      )}

      {emailPreviewed && <EmailPreviewPanel template={emailPreviewed} onClose={() => setEmailPreviewId(null)} />}

      {previewed && (
        <div className='fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]'>
          <div className='max-w-[1400px] mx-auto px-6 py-4'>
            <div className='flex items-start justify-between gap-4'>
              <div className='min-w-0'>
                <h3 className='text-lg font-semibold text-slate-900'>{previewed.name}</h3>
                <div className='mt-2'>
                  <MessageBubble text={previewed.text} />
                </div>
                {previewed.full_short_url && (
                  <p className='mt-2 text-xs text-slate-500'>
                    Short link: {previewed.full_short_url}
                    {previewed.destination_url && <> → {previewed.destination_url}</>}
                  </p>
                )}
              </div>
              <div className='flex items-center gap-2'>
                <button
                  onClick={() => setModal({ mode: 'edit', template: previewed })}
                  className='px-3 py-1.5 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded'
                >
                  Edit
                </button>
                <button onClick={() => setPreviewId(null)} className='text-slate-400 hover:text-slate-600'>
                  ✕
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsTemplates;
